import React, { useState, useEffect, useRef } from 'react';
import { 
  getUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  deleteNotification 
} from '../../utils/api';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { AtSign, Bell, BarChart3, CornerDownRight, Megaphone, TriangleAlert } from 'lucide-react';
import './NotificationCenter.css';

const NotificationCenter = ({ isOpen, onClose, onNavigate, onNotificationRead }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const { userSettings } = useUserSettingsContext();
  const panelRef = useRef(null);

  // Load notifications when panel opens
  useEffect(() => {
    if (isOpen && userSettings?.email) {
      loadNotifications();
    }
  }, [isOpen, userSettings?.email]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Load notifications from API
  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const notificationData = await getUserNotifications(userSettings.email);
      setNotifications(notificationData);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if unread
      if (!notification.isRead) {
        await markNotificationAsRead(notification.id, userSettings.email);
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
        // Notify parent component to refresh unread count
        if (onNotificationRead) {
          onNotificationRead();
        }
      }

      // Navigate to the relevant location
      if (notification.actionUrl && onNavigate) {
        onNavigate(notification.actionUrl, notification);
      }

      // Close the notification center
      onClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(userSettings.email);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      // Notify parent component to refresh unread count
      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId, event) => {
    event.stopPropagation(); // Prevent click from bubbling to notification click
    
    try {
      await deleteNotification(notificationId, userSettings.email);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'mention': return <AtSign size={16} />;
      case 'note_reply': return <CornerDownRight size={16} />;
      case 'report_shared': return <BarChart3 size={16} />;
      case 'system': return <Bell size={16} />;
      default: return <Megaphone size={16} />;
    }
  };

  const isMention = (n) => n.type === 'mention' || n.type === 'note_reply';
  const visibleNotifications = notifications.filter((n) => (
    filter === 'mentions' ? isMention(n) : filter === 'unread' ? !n.isRead : true
  ));
  // "Version · Account" — skips whichever part is missing.
  const contextLine = (c) => [c?.reportName, c?.pageName].filter(Boolean).join(' · ');

  // Group notifications by date
  const groupedNotifications = visibleNotifications.reduce((groups, notification) => {
    const date = new Date(notification.createdAt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let groupKey;
    if (date.toDateString() === today.toDateString()) {
      groupKey = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupKey = 'Yesterday';
    } else {
      groupKey = date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      });
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(notification);
    return groups;
  }, {});

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (!isOpen) return null;

  return (
    <div className="notification-center-overlay">
      <div className="notification-center" ref={panelRef}>
        {/* Header */}
        <div className="notification-header">
          <div className="notification-title">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className="notification-unread-badge">{unreadCount}</span>
            )}
          </div>
          <div className="notification-actions">
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                title="Mark all as read"
              >
                Mark all read
              </button>
            )}
            <button className="notification-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className="notification-filters" role="group" aria-label="Show">
          {[['all', 'All'], ['mentions', 'Mentions'], ['unread', 'Unread']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              className={`notification-filter ${filter === value ? 'active' : ''}`}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="notification-content">
          {loading && (
            <div className="notification-loading">
              <div className="loading-spinner"></div>
              Loading notifications...
            </div>
          )}

          {error && (
            <div className="notification-error">
              <div className="error-icon"><TriangleAlert size={20} /></div>
              <div className="error-text">{error}</div>
              <button onClick={loadNotifications} className="retry-btn">
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && visibleNotifications.length === 0 && (
            <div className="notification-empty">
              <div className="empty-icon"><Bell size={24} /></div>
              <div className="empty-title">{filter === 'mentions' ? 'No mentions' : filter === 'unread' ? 'Nothing unread' : 'No notifications'}</div>
              <div className="empty-subtitle">You're all caught up!</div>
            </div>
          )}

          {!loading && !error && visibleNotifications.length > 0 && (
            <div className="notification-list">
              {Object.entries(groupedNotifications).map(([dateGroup, groupNotifications]) => (
                <div key={dateGroup} className="notification-group">
                  <div className="notification-group-header">{dateGroup}</div>
                  {groupNotifications.map(notification => (
                    <div 
                      key={notification.id}
                      className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="notification-body">
                        <div className="notification-message">
                          <span className="notification-title-text">{notification.title}</span>
                          {notification.message && (
                            <span className={`notification-message-text ${isMention(notification) ? 'quote' : ''}`}>{notification.message}</span>
                          )}
                        </div>
                        <div className="notification-meta">
                          <span className="notification-time">
                            {formatRelativeTime(notification.createdAt)}
                          </span>
                          {contextLine(notification.context) && (
                            <span className="notification-context">
                              {contextLine(notification.context)}
                            </span>
                          )}
                        </div>
                      </div>
                      <button 
                        className="notification-delete"
                        onClick={(e) => handleDeleteNotification(notification.id, e)}
                        title="Delete notification"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;